import React, { useEffect, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { ValueProposition } from './components/ValueProposition';
import { About } from './components/About';
import { Services } from './components/Services';
import { WhyChooseUs } from './components/WhyChooseUs';
import { Gallery } from './components/Gallery';
import { Contact } from './components/Contact';
import { Footer } from './components/Footer';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { TermsOfService } from './components/TermsOfService';
import { CookiePolicy } from './components/CookiePolicy';
import { NotFound } from './components/NotFound';
import { CookieConsent } from './components/CookieConsent';
import { LanguagePreference } from './components/LanguagePreference';
import { AdminLoginModal } from './components/AdminLoginModal';
import { usePageMeta } from './hooks/usePageMeta';

const Careers = lazy(() => import('./pages/Careers'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const JobManager = lazy(() => import('./pages/JobManager'));

// Handles scroll behavior on route change and hash change
function ScrollToHash() {
  const { pathname, hash, key } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      let attempts = 0;
      let cancelled = false;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (attempts++ < 30) {
          // Retry ~every 100ms for up to 3s while route content mounts
          setTimeout(tryScroll, 100);
        }
      };
      tryScroll();
      return () => {
        cancelled = true;
      };
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [pathname, hash, key]);
  return null;
}

function HomePage() {
  usePageMeta({
    title: 'Industrial Engineering Support in China | Tower Technology',
    description:
      'Tower Technology provides engineers, site supervisors and technical specialists for industrial installation, commissioning, automation and project execution across China.',
    canonical: 'https://towertech-eng.com/',
    ogTitle: 'Industrial Engineering Support in China | Tower Technology',
    ogDescription:
      'Engineers, supervisors and technical specialists supporting industrial installation, commissioning and project execution across China.',
  });

  return (
    <>
      <Header />
      <main>
        <Hero />
        <ValueProposition />
        <About />
        <Services />
        <WhyChooseUs />
        <Gallery />
        <Contact />
      </main>
      <Footer />
    </>
  );
}

function App() {
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
  }, []);

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <ScrollToHash />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/:slug" element={<JobDetail />} />
            <Route path="/admin/jobs" element={<JobManager />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <LanguagePreference />
        <CookieConsent />
        <AdminLoginModal />
      </BrowserRouter>
    </div>
  );
}

export default App;
